import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dropdown,
  DropdownItem,
  Modal,
  Text,
} from '@amzn/storm-ui';
import { MessageBundle } from '@amzn/arb-tools';
import { AgreementDocument } from '@services/activation/activation';
import {
  AGREEMENT_MODAL_HEADER_KEY,
  AGREEMENT_LANGUAGE_LABEL_KEY,
  AGREEMENT_TITLE_KEY,
  AGREEMENT_ERROR_HEADER_KEY,
  AGREEMENT_ERROR_BODY_KEY,
  AGREEMENT_CANCEL_KEY,
  AGREEMENT_ACCEPT_KEY,
} from '../constants';

interface AgreementModalProps {
  intl: MessageBundle;
  isOpen: boolean;
  agreements: AgreementDocument[];
  onClose: () => void;
  onAccept: () => void;
}

const AgreementModal: FC<AgreementModalProps> = ({
  intl,
  isOpen,
  agreements,
  onClose,
  onAccept,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (isOpen) setSelectedIndex(0);
  }, [isOpen]);

  const hasContent = !!agreements[selectedIndex]?.document?.length;

  const footer = (
    <>
      <Button onClick={onClose}>{intl.getMessage(AGREEMENT_CANCEL_KEY)}</Button>
      <Button
        onClick={onAccept}
        variant="primary"
        disabled={!hasContent}
        id="agreement-positive-btn"
      >
        {intl.getMessage(AGREEMENT_ACCEPT_KEY)}
      </Button>
    </>
  );

  return (
    <Modal
      header={intl.getMessage(AGREEMENT_MODAL_HEADER_KEY)}
      isOpen={isOpen}
      onClose={onClose}
      footer={footer}
      id="agreement-modal"
    >
      <Dropdown
        label={intl.getMessage(AGREEMENT_LANGUAGE_LABEL_KEY)}
        inline
        id="agreement-language-selection"
        selectedValue={selectedIndex}
        onChange={(value: number) => setSelectedIndex(value)}
      >
        {agreements.map((agreement, index) => (
          <DropdownItem key={agreement.languageCode} value={index}>
            {agreement.languageDisplayText}
          </DropdownItem>
        ))}
      </Dropdown>
      <h1>{intl.getMessage(AGREEMENT_TITLE_KEY)}</h1>
      {/* Content is trusted HTML from Hades (Amazon's document management service) */}
      {hasContent ? (
        <div
          dangerouslySetInnerHTML={{
            __html: agreements[selectedIndex].document,
          }}
        />
      ) : (
        <Alert
          variant="warning"
          header={intl.getMessage(AGREEMENT_ERROR_HEADER_KEY)}
        >
          <Text>{intl.getMessage(AGREEMENT_ERROR_BODY_KEY)}</Text>
        </Alert>
      )}
    </Modal>
  );
};

export default AgreementModal;
